/**
 * SettingsPanel — defaults locales del panel QA + test conectividad LLM.
 *
 * Persiste en localStorage 'qa_default_endpoint' como { name, customUrl }.
 * Solo afecta defaults de formularios del panel (ej: NewRunModal en Test Runs).
 *
 * Endpoint:
 *   GET /api/qa/llm/health?endpoint=heroku|price|custom&url=... → { ok, status, version, endpoint, url, error? }
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import './SettingsPanel.css';

const LS_KEY = 'qa_default_endpoint';
const ENDPOINTS = [
  { value: 'heroku', label: 'heroku' },
  { value: 'price',  label: 'price' },
  { value: 'custom', label: 'custom (URL personalizada)' },
];

function readDefaults() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { name: 'price', customUrl: '' };
    const parsed = JSON.parse(raw);
    return {
      name: parsed?.name || 'price',
      customUrl: parsed?.customUrl || '',
    };
  } catch (_) {
    return { name: 'price', customUrl: '' };
  }
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  return (
    <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
      {toast.message}
    </div>
  );
}

export default function SettingsPanel() {
  const initial = readDefaults();
  const [name,      setName]      = useState(initial.name);
  const [customUrl, setCustomUrl] = useState(initial.customUrl);
  const [toast,     setToast]     = useState(null);
  const [health,    setHealth]    = useState(null);
  const [checking,  setChecking]  = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // Auto-check del endpoint actual al cargar (read-only, no escribe LS)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = new URLSearchParams({ endpoint: initial.name });
        if (initial.name === 'custom' && initial.customUrl) {
          qs.set('url', initial.customUrl);
        }
        const res = await fetch(`${API_BASE}/api/qa/llm/health?${qs.toString()}`);
        const j = await res.json().catch(() => ({}));
        if (alive) setHealth({ ok: res.ok && j.ok !== false, data: j });
      } catch (e) {
        if (alive) setHealth({ ok: false, data: { error: e.message } });
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (name === 'custom' && !customUrl.trim()) {
      showToast('Custom URL es requerida.', 'error');
      return;
    }
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        name,
        customUrl: name === 'custom' ? customUrl.trim() : '',
      }));
      showToast('Defaults guardados (localStorage).');
    } catch (e) {
      showToast(`Error al guardar: ${e.message}`, 'error');
    }
  };

  const handleClear = () => {
    try {
      localStorage.removeItem(LS_KEY);
      setName('price');
      setCustomUrl('');
      showToast('Defaults limpiados.');
    } catch (e) {
      showToast(`Error: ${e.message}`, 'error');
    }
  };

  const handleHealthCheck = async () => {
    if (name === 'custom' && !customUrl.trim()) {
      showToast('Ingresá una custom URL para testear.', 'error');
      return;
    }
    setChecking(true);
    setHealth(null);
    try {
      const qs = new URLSearchParams({ endpoint: name });
      if (name === 'custom') qs.set('url', customUrl.trim());
      const res = await fetch(`${API_BASE}/api/qa/llm/health?${qs.toString()}`);
      const j = await res.json().catch(() => ({}));
      setHealth({ ok: res.ok && j.ok !== false, data: j });
      if (res.ok && j.ok !== false) {
        showToast('Endpoint OK');
      } else {
        showToast('Endpoint no responde correctamente', 'error');
      }
    } catch (e) {
      setHealth({ ok: false, data: { error: e.message } });
      showToast(e.message, 'error');
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <div className="um-card">
        <div className="qa-settings-section">
          <h3 className="qa-settings-section-title">Endpoint LLM por default</h3>
          <p className="qa-settings-section-desc">
            Este valor se usa como pre-seleccionado en los formularios del panel
            (ej: lanzar un test run). No afecta la elección del backend en producción
            ni cambia el endpoint que usa el catalogador IA en runtime.
          </p>

          <div className="qa-radio-group">
            {ENDPOINTS.map(e => (
              <label key={e.value} className="qa-radio-row">
                <input
                  type="radio"
                  name="qa-default-endpoint"
                  value={e.value}
                  checked={name === e.value}
                  onChange={() => setName(e.value)}
                />
                <span>{e.label}</span>
              </label>
            ))}
          </div>

          {name === 'custom' && (
            <input
              type="text"
              className="qa-settings-input"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://mi-endpoint-llm.example.com"
            />
          )}

          <div className="qa-settings-actions">
            <button className="um-btn um-btn-primary" onClick={handleSave}>
              Guardar defaults
            </button>
            <button className="um-btn um-btn-ghost" onClick={handleClear}>
              Limpiar
            </button>
            <button
              className="um-btn um-btn-ghost"
              onClick={handleHealthCheck}
              disabled={checking}
            >
              {checking ? 'Testeando...' : 'Test conectividad'}
            </button>
          </div>

          <div className="qa-settings-note">
            Guardado solo en este navegador (<code>localStorage['qa_default_endpoint']</code>).
            No se sincroniza entre devices ni usuarios.
          </div>

          {health && (
            <div className={`qa-health-card ${health.ok ? 'ok' : 'err'}`}>
              <div>
                <strong>{health.ok ? 'Endpoint OK' : 'Endpoint con problemas'}</strong>
                {health.data?.endpoint && ` — ${health.data.endpoint}`}
                {health.data?.version && ` (v${health.data.version})`}
              </div>
              {health.data?.url && (
                <div style={{ fontSize: '0.74rem', marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                  {health.data.url}
                </div>
              )}
              <pre>{JSON.stringify(health.data, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
